import { useMutation, useQuery, useQueryClient } from 'react-query';
import ProjectNotesAPI, {
  ProjectNotePayload,
  ProjectNoteResponse,
  UpdateNotePayload,
} from 'services/API/ProjectNotesAPI';
import { DetailedProjectNote } from 'types';

export const useProjectNotes = () => {
  const queryClient = useQueryClient();

  const invalidateLeadListNotes = () => {
    queryClient.invalidateQueries({ queryKey: ['filtered-group-config-leads'] });
  };

  const createProjectNote = useMutation(
    (payload: ProjectNotePayload) => ProjectNotesAPI.createProjectNote(payload),
    { onSuccess: invalidateLeadListNotes },
  );

  const updateProjectNote = useMutation(
    ({
      targetId,
      projectId,
      payload,
    }: {
      targetId?: number | string;
      projectId?: number;
      payload: UpdateNotePayload;
    }) => ProjectNotesAPI.updateProjectNote(targetId ?? projectId ?? 0, payload),
    { onSuccess: invalidateLeadListNotes },
  );

  const getProjectNote = (targetId: number | string) =>
    useQuery<ProjectNoteResponse>(
      ['projectNote', targetId],
      async () => {
        const result = await ProjectNotesAPI.getProjectNote(targetId);
        if (result === null) {
          throw new Error('API returned null');
        }

        return result;
      },
      {
        refetchOnWindowFocus: false,
      },
    );

  const getClientProjectNotes = (targetId: number | string) =>
    useQuery<DetailedProjectNote[]>(
      ['projectNotesByClient', targetId],
      async () => {
        const result = await ProjectNotesAPI.getClientProjectNotes(targetId);
        if (result === null) {
          throw new Error('API returned null');
        }

        return result;
      },
      {
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60,
      },
    );

  const deleteProjectNote = useMutation(
    (targetId: number | string) => ProjectNotesAPI.deleteProjectNote(targetId),
    { onSuccess: invalidateLeadListNotes },
  );

  return {
    createProjectNote,
    updateProjectNote,
    getProjectNote,
    getClientProjectNotes,
    deleteProjectNote,
  };
};
